<template>
  <!-- 2026-09-22：所有管理功能共用顶部蓝色标题说明，正文不再重复占用大块介绍区域。 -->
  <div class="function-navigation">
    <button class="back-icon" type="button" aria-label="返回上一页" title="返回上一页" @click="goback">
      <svg-icon icon="prev" aria-hidden="true"></svg-icon>
    </button>
    <div class="function-navigation-content">
      <div class="function-heading">
        <h1 class="function-title">{{ functionTitle }}</h1>
        <p class="function-description">{{ functionDescription }}
          <!-- 2026-09-22：配置有效视频才显示说明右侧录像机入口，未配置不占位。 -->
          <button v-if="functionVideoUrl" class="function-video-button" type="button" aria-label="播放功能演示视频" title="播放功能演示视频" @click="guideVideoVisible = true">
            <!-- 2026-09-27：录像机后增加同说明字号的白色帮助视频文字，整个按钮沿用原播放操作。 -->
            <svg viewBox="0 0 28 24" aria-hidden="true"><rect x="2" y="4" width="17" height="14" rx="3"/><path d="M19 8l7-3v12l-7-3M6 18l-2 4m11-4 2 4"/><path d="m9 8 5 3-5 3z"/></svg>
            <span>帮助视频</span>
          </button>
        </p>
      </div>
      <!-- 2026-09-22：续费提醒独立保留红字、无背景及单行展示，不混入蓝色功能说明。 -->
      <div class="membership-reminder" v-if="reminderText" role="status">{{ reminderText }}</div>
    </div>
  </div>
  <FunctionGuideVideo :visible="guideVideoVisible" :url="functionVideoUrl" :title="functionTitle" @close="guideVideoVisible = false" />
</template>
<script lang="ts">
import { useRoute, RouteLocationMatched } from 'vue-router'
import { watch, ref, computed, defineComponent, onMounted, onBeforeUnmount } from 'vue'
import { generateTitle as translateTitle } from '@/utils/lang'
// 2026-09-17：平台页面面包屑使用新栏目名称，分店仍保留原名称。
import { administrationTitleKey } from '@/utils/platformAdministration'
import { isNull } from '@/utils/route'
import sysRequest from '@/api/sys'
import { branchMembershipReminder, selectBranchMembership, BranchMembershipInfo } from '@/utils/branchMembershipReminder'
import store from '@/store'
import { resolveAdminFunctionDescription } from '@/utils/adminFunctionDescriptions'
// 2026-09-22：顶部采用平台保存的用途/视频，空配置继续使用原平台或分店用途说明。
import { findFunctionGuide } from '@/utils/functionGuides'
import { functionGuides, refreshFunctionGuides, resetFunctionGuides } from '@/utils/functionGuideState'
import FunctionGuideVideo from '@/components/FunctionGuideVideo.vue'
export default defineComponent({
components: { FunctionGuideVideo },
setup(){
  const generateTitle=(title)=>translateTitle(administrationTitleKey(title,store.getters.userInfo.orgId))
  const breadPath = ref<RouteLocationMatched[]>([])
  const route = useRoute()
  breadPath.value = route.matched.filter((r) => !isNull(r.meta))

  // 2026-09-22：标题跟随当前最深层路由，动态菜单名称兜底，平台/分店沿用各自栏目命名。
  const functionTitle = computed(() => {
    const leaf = breadPath.value[breadPath.value.length - 1]
    const menus = store.getters.functions?.allFunc || []
    const menu = menus.find(item => String(item.functionUrl || '').split(/[?#]/, 1)[0].toLowerCase() === route.path.toLowerCase())
    return translateTitle(administrationTitleKey(leaf?.meta.title || menu?.functionKey || '', store.getters.userInfo?.orgId), menu?.functionName) || '工作台'
  })
  const activeGuide = computed(() => findFunctionGuide(functionGuides.value, route.path))
  const functionDescription = computed(() => activeGuide.value?.description || resolveAdminFunctionDescription(route.path, functionTitle.value, Number(store.getters.userInfo?.orgId)))
  const functionVideoUrl = computed(() => activeGuide.value?.videoUrl || '')
  const guideVideoVisible = ref(false)
  watch(functionVideoUrl, () => { guideVideoVisible.value = false })
  const currentPath = ref('')
  const goback = () => {
    window.history.back()
  }
  // 2026-09-22：返回管理端/切换页面及可见时每分钟刷新；清理旧账号、旧请求和定时器，避免续费后残留提示。
  const memberInfo = ref<BranchMembershipInfo>()
  const currentTime = ref(Date.now())
  const sessionKey = computed(() => JSON.stringify([
    store.getters.token, store.getters.userInfo?.orgId, store.getters.userInfo?.id, store.getters.userInfo?.memberCustomerId
  ]))
  const reminderText = computed(() => branchMembershipReminder(memberInfo.value, currentTime.value))
  let mounted = false
  let requestVersion = 0
  let pendingVersion: number | undefined
  let lastRefreshed = 0
  let refreshTimer: number | undefined
  const refreshMembership = async (force = false) => {
    currentTime.value = Date.now()
    const orgId = Number(store.getters.userInfo?.orgId)
    if (!mounted || document.hidden || !store.getters.token || !(orgId > 1000)) return
    if (pendingVersion !== undefined || (!force && currentTime.value - lastRefreshed < 30000)) return
    const key = sessionKey.value
    const version = ++requestVersion
    pendingVersion = version
    try {
      const res = await sysRequest.getMemberInfo(orgId)
      if (mounted && version === requestVersion && key === sessionKey.value) {
        memberInfo.value = selectBranchMembership(Array.isArray(res.data) ? res.data : [], store.getters.userInfo?.memberCustomerId)
        lastRefreshed = Date.now()
      }
    } catch {
      // 短暂网络失败保留上次已确认状态，下一次刷新自动重试，不弹出重复错误。
    } finally {
      if (pendingVersion === version) pendingVersion = undefined
    }
  }
  const guideSession = () => store.getters.token ? sessionKey.value : ''
  const refreshGuides = () => {
    if (mounted && !document.hidden) void refreshFunctionGuides(guideSession())
  }
  const refreshOnReturn = () => { void refreshMembership(true); refreshGuides() }
  watch(sessionKey, () => {
    guideVideoVisible.value = false
    resetFunctionGuides(guideSession())
    refreshGuides()
    memberInfo.value = undefined
    lastRefreshed = 0
    requestVersion++
    pendingVersion = undefined
    void refreshMembership(true)
  })
  watch(route, () => {
    guideVideoVisible.value = false
    refreshGuides()
    breadPath.value = route.matched.filter((r) => !isNull(r.meta))
    currentPath.value = route.path
    void refreshMembership()
  })
  onMounted(() => {
    mounted = true
    refreshOnReturn()
    window.addEventListener('focus', refreshOnReturn)
    document.addEventListener('visibilitychange', refreshOnReturn)
    refreshTimer = window.setInterval(() => { void refreshMembership() }, 60000)
  })
  onBeforeUnmount(() => {
    mounted = false
    requestVersion++
    window.clearInterval(refreshTimer)
    window.removeEventListener('focus', refreshOnReturn)
    document.removeEventListener('visibilitychange', refreshOnReturn)
  })
  return {
    generateTitle,currentPath,breadPath,goback,reminderText,functionTitle,functionDescription,functionVideoUrl,guideVideoVisible
  }
}
})

</script>
<style lang="scss" scoped>
// 2026-09-22：标题和用途并排显示，说明可完整换行；顶部随内容增高，窄屏不挤占右侧操作。
.function-navigation { display:flex; align-items:center; flex:1 1 0; min-width:0; gap:6px; }
.back-icon {
  display:flex; align-items:center; justify-content:center; flex:0 0 24px; width:24px; height:34px;
  padding:0; border:0; border-radius:4px; background:transparent; color:#8290a0; cursor:pointer;
  &:hover { background:#edf3f9; color:#0756a3; }
  &:focus-visible { outline:2px solid #1890ff; outline-offset:2px; }
}
.function-navigation-content { flex:1 1 0; min-width:0; }
// 2026-10-01：蓝色标题区限制桌面长度，剩余空间留白；说明按可用宽度自然换行，不截断内容。
.function-heading {
  display:flex; align-items:center; gap:16px; min-height:52px; max-width:1040px; box-sizing:border-box; padding:10px 16px; border-radius:8px;
  // 2026-09-27：左端使用主按钮蓝色，沿水平方向逐渐变浅。
  background:linear-gradient(90deg,var(--activeColor,#1890ff) 0%,#409eff 60%,#69b1ff 100%); color:#fff;
}
// 2026-09-27：统一为23px，比平台信息页25px的个人资料小一号，保持700加粗。
// 2026-10-01：按新截图取消功能标题加粗，保留既有23px字号。
.function-title { flex:0 1 auto; min-width:0; max-width:50%; margin:0; color:inherit; font-size:23px; font-weight:400; line-height:1.3; white-space:normal; overflow-wrap:anywhere; }
.function-description { flex:1 1 180px; min-width:0; margin:0; font-size:13px; font-weight:400; line-height:1.6; color:#f0f7ff; overflow-wrap:anywhere; }
/* 2026-09-22：录像机紧跟用途说明，键盘可操作且不改变标题区蓝底。 */
/* 2026-09-27：按钮随图标和文字自然宽度排版，白色文字继承说明的13px字号。 */
.function-video-button { display:inline-flex; vertical-align:middle; align-items:center; justify-content:center; gap:4px; min-height:28px; margin-left:8px; padding:3px; border:0; border-radius:4px; background:transparent; color:#fff; font:inherit; white-space:nowrap; cursor:pointer; }
.function-video-button svg { flex:none; width:25px; height:23px; fill:none; stroke:currentColor; stroke-width:1.7; stroke-linejoin:round; }
.function-video-button:hover { background:rgba(255,255,255,.16); }
.function-video-button:focus-visible { outline:2px solid white; outline-offset:2px; }
.membership-reminder {
  max-width:100%; min-width:0; margin-top:4px; padding:0; overflow-x:auto; white-space:nowrap;
  background:transparent; font-size:12px; color:red;
}
@media(max-width:1100px) {
  .function-heading { gap:12px; padding:9px 12px; }
}
@media(max-width:600px) {
  .function-heading { flex-wrap:wrap; gap:4px 10px; }
  .function-title { max-width:100%; }
  .function-description { flex-basis:100%; }
}
</style>
